import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    activatePricingVersion,
    getPricingConfig,
    publishPricingConfig,
    simulatePricing,
} from "./functions";
import { PRIORITY_KEYS, PRODUCT_KEYS } from "./pricingMeta";

const PRODUCT_FIELD_ORDER = ["baseCents", "active"];
const PRE_LACRE_FIELD_ORDER = ["withSubgradesCents", "withoutSubgradesCents"];
const PRIORITY_FIELD_ORDER = [
    "additionalRate",
    "daysPerItem",
    "discountPerItem",
    "maxDiscount",
    "minDeadlineDays",
    "maxItems",
    "hubItemsPerCase",
    "active",
];
const FEE_FIELD_ORDER = ["handlingFeeRate", "autographCents"];

// jest-environment-jsdom não expõe structuredClone; a config é sempre JSON-safe
// (números, strings, booleans), então o fallback via JSON é equivalente.
const cloneConfig = (value) => (
    typeof structuredClone === "function" ? structuredClone(value) : JSON.parse(JSON.stringify(value))
);

const isIntGte = (value, min) => Number.isInteger(value) && value >= min;
const isRate01 = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
const isPositiveNumber = (value) => typeof value === "number" && Number.isFinite(value) && value > 0;

// Espelha validatePricingConfig do backend (mgs-backend/api/utils/pricingConfigService.js)
// para dar feedback local antes de publicar.
const validateDraftConfig = (config) => {
    const errors = [];
    if (!config || typeof config !== "object") {
        return ["config ausente"];
    }

    for (const key of PRODUCT_KEYS) {
        const product = config.products?.[key];
        if (!product) {
            errors.push(`produto ${key} ausente`);
            continue;
        }
        if (!isIntGte(product.baseCents, 0)) {
            errors.push(`${key}.baseCents deve ser inteiro >= 0`);
        }
        if (typeof product.active !== "boolean") {
            errors.push(`${key}.active deve ser boolean`);
        }
    }

    const preLacre = config.products?.pre?.preLacre;
    if (!preLacre || !isIntGte(preLacre.withSubgradesCents, 0) || !isIntGte(preLacre.withoutSubgradesCents, 0)) {
        errors.push("pre.preLacre com centavos inteiros >= 0 é obrigatório");
    }

    for (const key of PRIORITY_KEYS) {
        const priority = config.priorities?.[key];
        if (!priority) {
            errors.push(`prioridade ${key} ausente`);
            continue;
        }
        // additionalRate pode passar de 1 (ex.: max = 4 → +400%)
        if (typeof priority.additionalRate !== "number" || !Number.isFinite(priority.additionalRate) || priority.additionalRate < 0) {
            errors.push(`${key}.additionalRate deve ser >= 0`);
        }
        if (!isPositiveNumber(priority.daysPerItem)) {
            errors.push(`${key}.daysPerItem deve ser > 0`);
        }
        if (!isRate01(priority.discountPerItem)) {
            errors.push(`${key}.discountPerItem deve estar entre 0 e 1`);
        }
        if (!isRate01(priority.maxDiscount)) {
            errors.push(`${key}.maxDiscount deve estar entre 0 e 1`);
        }
        if (!isIntGte(priority.minDeadlineDays, 1)) {
            errors.push(`${key}.minDeadlineDays deve ser inteiro >= 1`);
        }
        if (!isIntGte(priority.maxItems, 1)) {
            errors.push(`${key}.maxItems deve ser inteiro >= 1`);
        }
        if (!isIntGte(priority.hubItemsPerCase, 1)) {
            errors.push(`${key}.hubItemsPerCase deve ser inteiro >= 1`);
        }
        if (typeof priority.active !== "boolean") {
            errors.push(`${key}.active deve ser boolean`);
        }
    }

    if (!isRate01(config.fees?.handlingFeeRate)) {
        errors.push("fees.handlingFeeRate deve estar entre 0 e 1");
    }
    if (!isIntGte(config.fees?.autographCents, 0)) {
        errors.push("fees.autographCents deve ser inteiro >= 0");
    }
    if (config.products && !PRODUCT_KEYS.some((key) => config.products[key]?.active)) {
        errors.push("pelo menos um produto deve estar ativo");
    }
    if (config.priorities && !PRIORITY_KEYS.some((key) => config.priorities[key]?.active)) {
        errors.push("pelo menos uma prioridade deve estar ativa");
    }

    return errors;
};

const computeDiff = (before, after) => {
    const diffs = [];
    if (!before || !after) {
        return diffs;
    }

    for (const key of PRODUCT_KEYS) {
        const beforeProduct = before.products?.[key] || {};
        const afterProduct = after.products?.[key] || {};
        for (const field of PRODUCT_FIELD_ORDER) {
            if (beforeProduct[field] !== afterProduct[field]) {
                diffs.push({ path: `products.${key}.${field}`, before: beforeProduct[field], after: afterProduct[field] });
            }
        }
        if (key === "pre") {
            const beforePreLacre = beforeProduct.preLacre || {};
            const afterPreLacre = afterProduct.preLacre || {};
            for (const field of PRE_LACRE_FIELD_ORDER) {
                if (beforePreLacre[field] !== afterPreLacre[field]) {
                    diffs.push({
                        path: `products.pre.preLacre.${field}`,
                        before: beforePreLacre[field],
                        after: afterPreLacre[field],
                    });
                }
            }
        }
    }

    for (const key of PRIORITY_KEYS) {
        const beforePriority = before.priorities?.[key] || {};
        const afterPriority = after.priorities?.[key] || {};
        for (const field of PRIORITY_FIELD_ORDER) {
            if (beforePriority[field] !== afterPriority[field]) {
                diffs.push({ path: `priorities.${key}.${field}`, before: beforePriority[field], after: afterPriority[field] });
            }
        }
    }

    const beforeFees = before.fees || {};
    const afterFees = after.fees || {};
    for (const field of FEE_FIELD_ORDER) {
        if (beforeFees[field] !== afterFees[field]) {
            diffs.push({ path: `fees.${field}`, before: beforeFees[field], after: afterFees[field] });
        }
    }

    return diffs;
};

const setNestedProductField = (product, field, value) => {
    const next = cloneConfig(product || {});
    const parts = String(field).split(".");
    let target = next;
    for (let index = 0; index < parts.length - 1; index += 1) {
        target[parts[index]] = target[parts[index]] || {};
        target = target[parts[index]];
    }
    target[parts[parts.length - 1]] = value;
    return next;
};

const usePricingEditor = ({ token } = {}) => {
    const [status, setStatus] = useState("loading");
    const [activeVersion, setActiveVersion] = useState(null);
    const [versions, setVersions] = useState([]);
    const [activeConfig, setActiveConfig] = useState(null);
    const [draft, setDraft] = useState(null);
    const [simulation, setSimulation] = useState({ status: "idle", result: null });
    const simulationTimeoutRef = useRef(null);
    // Evita "state update on an unmounted component" quando o fetch (load) ou a simulação
    // debounced resolvem depois do hook desmontar.
    const isMountedRef = useRef(true);

    const load = useCallback(async () => {
        if (isMountedRef.current) {
            setStatus("loading");
        }
        const response = await getPricingConfig(token);
        if (!isMountedRef.current) {
            return response;
        }
        if (response?.type === "success") {
            const { activeVersion: version, config, versions: versionList } = response.data || {};
            setActiveVersion(version);
            setVersions(versionList || []);
            setActiveConfig(config);
            setDraft(cloneConfig(config));
            setStatus("ready");
        } else {
            setStatus("error");
        }
        return response;
    }, [token]);

    useEffect(() => {
        load();
        // Só recarrega quando o token muda; load() é estável para o mesmo token.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (simulationTimeoutRef.current) {
                clearTimeout(simulationTimeoutRef.current);
            }
        };
    }, []);

    const reload = useCallback(() => load(), [load]);

    const diff = useMemo(() => computeDiff(activeConfig, draft), [activeConfig, draft]);
    const isDirty = diff.length > 0;
    const validationErrors = useMemo(() => validateDraftConfig(draft), [draft]);

    const setProductField = useCallback((key, field, value) => {
        setDraft((current) => {
            if (!current) {
                return current;
            }
            return {
                ...current,
                products: {
                    ...current.products,
                    [key]: setNestedProductField(current.products?.[key], field, value),
                },
            };
        });
    }, []);

    const setPriorityField = useCallback((key, field, value) => {
        setDraft((current) => {
            if (!current) {
                return current;
            }
            return {
                ...current,
                priorities: {
                    ...current.priorities,
                    [key]: { ...current.priorities?.[key], [field]: value },
                },
            };
        });
    }, []);

    const setFeeField = useCallback((field, value) => {
        setDraft((current) => {
            if (!current) {
                return current;
            }
            return {
                ...current,
                fees: { ...current.fees, [field]: value },
            };
        });
    }, []);

    const toggleProductActive = useCallback((key) => {
        setDraft((current) => {
            if (!current) {
                return current;
            }
            const product = current.products?.[key] || {};
            return {
                ...current,
                products: {
                    ...current.products,
                    [key]: { ...product, active: !product.active },
                },
            };
        });
    }, []);

    const togglePriorityActive = useCallback((key) => {
        setDraft((current) => {
            if (!current) {
                return current;
            }
            const priority = current.priorities?.[key] || {};
            return {
                ...current,
                priorities: {
                    ...current.priorities,
                    [key]: { ...priority, active: !priority.active },
                },
            };
        });
    }, []);

    const publish = useCallback(async (note) => {
        if (validationErrors.length > 0) {
            return { type: "error", message: validationErrors[0] };
        }
        const response = await publishPricingConfig(token, { config: draft, note });
        if (response?.type === "success") {
            await reload();
        }
        return response;
    }, [draft, reload, token, validationErrors]);

    const activateVersion = useCallback(async (version) => {
        const response = await activatePricingVersion(token, version);
        if (response?.type === "success") {
            await reload();
        }
        return response;
    }, [reload, token]);

    const runSimulation = useCallback((simCase) => {
        if (simulationTimeoutRef.current) {
            clearTimeout(simulationTimeoutRef.current);
            simulationTimeoutRef.current = null;
        }

        if (validationErrors.length > 0) {
            // Cancela qualquer simulação pendente/"loading" — sem isso ela ficaria presa
            // em "loading" para sempre, já que o timer que a resolveria foi cancelado acima.
            setSimulation((current) => (
                current.status === "loading" ? { status: "idle", result: null } : current
            ));
            return;
        }

        // A UI monta os casos (comparativo de prioridades e/ou de quantidades);
        // o hook só debounce-ia e envia o lote.
        const cases = Array.isArray(simCase?.cases) ? simCase.cases : [];
        if (cases.length === 0) {
            return;
        }

        setSimulation({ status: "loading", result: null });
        simulationTimeoutRef.current = setTimeout(async () => {
            const response = await simulatePricing(token, { config: draft, cases });
            simulationTimeoutRef.current = null;
            if (!isMountedRef.current) {
                return;
            }
            if (response?.type === "success") {
                setSimulation({ status: "ready", result: response.data });
            } else {
                setSimulation({ status: "error", result: response || { message: "Erro na simulação." } });
            }
        }, 300);
    }, [draft, token, validationErrors]);

    return {
        status,
        draft,
        activeVersion,
        versions,
        isDirty,
        diff,
        validationErrors,
        simulation,
        setProductField,
        setPriorityField,
        setFeeField,
        toggleProductActive,
        togglePriorityActive,
        runSimulation,
        publish,
        activateVersion,
        reload,
    };
};

// Reuso do histórico: diff entre duas configs completas (mesma lógica do draft).
export { computeDiff as computeConfigDiff };

export default usePricingEditor;
